/**
 * Resilient multipart uploader for full-match master videos.
 *
 * 2026-08-14 — replaces the serial, zero-retry loop that lived inline in
 * both VideoTagging.jsx and MatchVideos.jsx. That loop failed with a bare
 * "[master-upload] Network Error" whenever ANY single part's TCP
 * connection dropped, and then discarded every part already in S3. On a
 * 10 GB master over a domestic uplink that is a ~2 hour upload; one blip
 * anywhere in it lost the lot. (Diagnosed from two orphaned multipart
 * uploads in zcd-recordings sitting at 0 and 19 parts.)
 *
 * What this does differently:
 *   - Retries each part with exponential backoff (network errors, 5xx,
 *     429 and timeouts are all transient at S3's scale).
 *   - Re-presigns a part on 403 / ExpiredToken. The original URLs carry a
 *     6 h TTL and a slow 10 GB upload can outlive that.
 *   - Uploads several parts concurrently, which both shortens the window
 *     in which something can go wrong and roughly triples throughput.
 *   - Never aborts on failure. The upload_id is handed back so the
 *     operator can resume and re-send only the missing parts; the bucket's
 *     zcd-abort-stuck-multipart-7d lifecycle rule reaps genuinely
 *     abandoned ones.
 *
 * Progress reporting stays byte-accurate under concurrency by tracking
 * per-part byte counts rather than a single running total.
 */
import axios from "axios";

// Raw axios for presigned PUTs — the app-wide `api` instance injects an
// Authorization header (and a 30 s timeout) that would break the S3
// signature and kill long part uploads respectively.
const rawPut = (url, body, config) =>
  axios.put(url, body, { timeout: 0, ...config });

export const PART_RETRY_MAX = 5;      // attempts per part, incl. the first
const BACKOFF_BASE_MS = 1500;         // 1.5s, 3s, 6s, 12s
const DEFAULT_CONCURRENCY = 3;

// 2026-08-18 — operators hit "S3 400: ExpiredToken — The provided token
// has expired." mid-upload. A presigned URL dies with the credentials
// that signed it, and prod signs with the EC2 instance role, whose STS
// token rotates every few hours: the URLs can lapse long before their
// nominal 6 h TTL. The backend now reports the real `expires_at`, so
// re-presign a part this long before that moment rather than discovering
// it by pushing megabytes into a 400.
const URL_REFRESH_MARGIN_MS = 60 * 1000;

// 2026-09-01 — the last step, CompleteMultipartUpload, is the one step
// whose duration we do not control: S3 needs 60-120 s to assemble a
// multi-GB master, and CloudFront abandons the origin request at 30 s
// and hands us **504**. That 504 said nothing about the upload, which
// had in fact landed: a 3.7 GB Baroda master showed
// "[master-upload] HTTP 504" and was then re-uploaded from scratch for
// nothing. So a slow or cut-off completion is no longer a failure —
// wait for the object to appear, and only give up if it never does.
const ASSEMBLE_POLL_MS = 5000;
const ASSEMBLE_TIMEOUT_MS = 20 * 60 * 1000;   // 20 min, then call it dead
const COMPLETE_POST_TIMEOUT_MS = 40000;       // outlast CloudFront's 30 s

/**
 * Could this completion still be running at S3 despite the error?
 * A gateway timeout, a dropped connection or a client-side timeout all
 * mean "no answer", never "it failed" — S3 finishes regardless.
 */
function isAssemblyPending(e) {
  if (e?.__zcdFatal || isCancel(e)) return false;
  const s = e?.response?.status;
  if (s === undefined) return true;             // ERR_NETWORK / ECONNABORTED
  return s === 408 || s === 502 || s === 503 || s === 504;
}

/**
 * Poll the match's video state until the master lands in S3.
 * Returns the master object, or null if it never showed up in time.
 */
async function waitForMaster({ api, matchId, s3KeyWithin, signal, onTick }) {
  const until = Date.now() + ASSEMBLE_TIMEOUT_MS;
  for (;;) {
    if (signal?.aborted) return null;
    try {
      const r = await api.get(
        `/api/admin/v2/matches/${matchId}/whole-match-video/`,
      );
      const master = r.data?.master;
      if (master && master.s3_key_within === s3KeyWithin) return master;
    } catch { /* the poll itself is allowed to fail — keep waiting */ }
    if (Date.now() >= until) return null;
    onTick?.();
    await sleep(ASSEMBLE_POLL_MS);
  }
}

/** When do the presigned URLs in this response stop working? (ms epoch) */
function urlDeadline(data) {
  const at = Date.parse(data?.expires_at || "");
  if (!Number.isNaN(at)) return at;
  const secs = Number(data?.expires_in);
  // No deadline reported (an older backend) — treat the URLs as
  // indefinitely valid and fall back to the reactive 403/400 path.
  return Number.isFinite(secs) && secs > 0 ? Date.now() + secs * 1000 : null;
}

const LS_PREFIX = "zcd:master-upload:";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Did the caller (or the operator's Cancel button) stop this upload? */
function isCancel(e) {
  return !!e?.__zcdCancelled
    || e?.code === "ERR_CANCELED"
    || e?.name === "CanceledError";
}

/** Is this failure worth retrying with the same URL? */
function isTransient(e) {
  if (e?.__zcdFatal || isCancel(e)) return false;
  if (!e?.response) return true;              // ERR_NETWORK, ECONNRESET, CORS-masked
  const s = e.response.status;
  return s === 429 || (s >= 500 && s <= 599);
}

/** Did the presigned URL expire (or otherwise stop being accepted)? */
function isExpiredUrl(e) {
  const s = e?.response?.status;
  if (s !== 403 && s !== 400) return false;
  const body = typeof e?.response?.data === "string" ? e.response.data : "";
  return /Expired|expired|AccessDenied|SignatureDoesNotMatch/.test(body) || !body;
}

// ── Resume slot (survives a refresh / browser restart) ───────────────

export function loadResumeSlot(matchId) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + matchId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function saveResumeSlot(matchId, slot) {
  try {
    localStorage.setItem(LS_PREFIX + matchId, JSON.stringify(slot));
  } catch { /* quota / privacy mode — resume just won't survive a refresh */ }
}

export function clearResumeSlot(matchId) {
  try { localStorage.removeItem(LS_PREFIX + matchId); }
  catch { /* noop */ }
}

/**
 * Mirror of the backend's `slug()` (scoring/services/match_video_aligner)
 * — the S3 key stores an underscore-slugged filename, so a slot
 * discovered from S3 has to be compared against a slugged File.name.
 */
function slugName(name) {
  const [stem, ext] = (() => {
    const i = String(name || "").lastIndexOf(".");
    return i <= 0 ? [String(name || ""), "mp4"]
      : [String(name).slice(0, i), String(name).slice(i + 1)];
  })();
  const s = stem.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "unknown";
  return `${s}.${ext.replace(/[^A-Za-z0-9]/g, "").toLowerCase() || "mp4"}`;
}

/**
 * Does the file the operator just picked match the one a stored resume
 * slot was opened for? Name + byte size is as much identity as the File
 * API gives us without hashing 10 GB.
 *
 * Slots discovered from S3 (rather than localStorage) know only the
 * slugged filename — there is no record of the original byte count — so
 * those match on name alone. A wrong guess is caught at the end anyway:
 * CompleteMultipartUpload rejects a mismatched part set.
 */
export function slotMatchesFile(slot, file) {
  if (!slot || !file) return false;
  if (slot.discovered) return slot.filename === slugName(file.name);
  return slot.filename === file.name
    && Number(slot.totalSize) === Number(file.size);
}

/**
 * Ask the backend whether this match has a half-finished upload sitting
 * in S3. Used when localStorage has nothing — a different browser, a
 * cleared cache, or an upload that failed before resume support existed.
 * Returns a resume slot or null.
 */
export async function discoverResumable({ api, matchId }) {
  try {
    const r = await api.post(
      `/api/admin/v2/matches/${matchId}/resume-multipart-upload-whole-match/`,
      {},
    );
    const best = (r.data?.in_progress || [])
      .find((u) => u.parts_uploaded > 0 && u.part_size > 0);
    if (!best) return null;
    return {
      uploadId: best.upload_id,
      s3KeyWithin: best.s3_key_within,
      partSize: best.part_size,
      numParts: null,               // derived from the picked file's size
      filename: best.filename,
      totalSize: null,
      bytesUploaded: best.bytes_uploaded,
      partsUploaded: best.parts_uploaded,
      discovered: true,
    };
  } catch {
    return null;                    // resume is a bonus, never a blocker
  }
}

// ── The uploader ─────────────────────────────────────────────────────

/**
 * Upload `file` as the match master, resuming `resume` if supplied.
 *
 * @param {object}   o
 * @param {object}   o.api          the app axios instance (auth + baseURL)
 * @param {string}   o.matchId
 * @param {File}     o.file
 * @param {object}   [o.resume]     slot from loadResumeSlot()
 * @param {function} [o.onProgress] ({pct, loaded, total, phase, partsDone,
 *                                    partsTotal, retrying}) => void
 * @param {AbortSignal} [o.signal]  cancel the whole upload
 * @param {number}   [o.concurrency]
 * @returns {Promise<{s3_key: string, s3_key_within: string}>}
 * @throws  Error with `.uploadId` / `.s3KeyWithin` / `.resumable` attached
 *          so the caller can offer Resume instead of a dead end.
 */
export async function uploadMaster({
  api, matchId, file, resume = null,
  onProgress = () => {}, signal, concurrency = DEFAULT_CONCURRENCY,
}) {
  const cancelled = () => {
    const e = new Error("Upload cancelled.");
    e.__zcdFatal = true;
    e.__zcdCancelled = true;
    return e;
  };
  const throwIfAborted = () => { if (signal?.aborted) throw cancelled(); };

  // When one part gives up, the workers still mid-PUT are left to finish
  // (their bytes make the next resume cheaper) — but their progress
  // callbacks must not repaint a progress bar the caller has already
  // torn down.
  let settled = false;

  let uploadId, s3KeyWithin, s3Key, partSize, numParts;
  const urlByPart = new Map();   // partNumber -> {url, expiresAt|null}
  const rememberUrls = (list, deadline) => {
    for (const p of list || []) {
      urlByPart.set(p.part_number, { url: p.url, expiresAt: deadline });
    }
  };
  const doneEtags = new Map();   // partNumber -> ETag

  onProgress({
    pct: 0, loaded: 0, total: file.size, phase: "start-multipart",
    partsDone: 0, partsTotal: 0,
  });

  if (resume?.uploadId) {
    // ── Resume: ask S3 what it already has, presign only the rest ──
    uploadId = resume.uploadId;
    s3KeyWithin = resume.s3KeyWithin;
    partSize = resume.partSize;
    // A slot discovered from S3 carries no part count — the part size is
    // fixed for the life of the upload, so the picked file gives it.
    numParts = resume.numParts || Math.ceil(file.size / partSize);
    const all = Array.from({ length: numParts }, (_, i) => i + 1);
    const r = await api.post(
      `/api/admin/v2/matches/${matchId}/resume-multipart-upload-whole-match/`,
      { upload_id: uploadId, s3_key_within: s3KeyWithin, part_numbers: all },
    );
    s3Key = r.data.s3_key;
    for (const p of r.data.uploaded_parts || []) {
      doneEtags.set(p.part_number, p.etag);
    }
    rememberUrls(r.data.part_urls, urlDeadline(r.data));
  } else {
    // ── Fresh start ────────────────────────────────────────────────
    const start = await api.post(
      `/api/admin/v2/matches/${matchId}/start-multipart-upload-whole-match/`,
      { filename: file.name, total_size: file.size },
    );
    uploadId = start.data.upload_id;
    s3KeyWithin = start.data.s3_key_within;
    s3Key = start.data.s3_key;
    partSize = start.data.part_size;
    numParts = start.data.num_parts
      ?? Math.ceil(file.size / start.data.part_size);
    rememberUrls(start.data.part_urls, urlDeadline(start.data));
  }

  // Persist immediately — if the tab dies mid-upload the operator can
  // still pick the same file and resume rather than restart.
  saveResumeSlot(matchId, {
    uploadId, s3KeyWithin, partSize, numParts,
    filename: file.name, totalSize: file.size,
    startedAt: new Date().toISOString(),
  });

  const partBounds = (n) => {
    const from = (n - 1) * partSize;
    return [from, Math.min(from + partSize, file.size)];
  };

  // Bytes already banked by parts S3 confirmed on resume.
  const sentByPart = new Map();
  for (const n of doneEtags.keys()) {
    const [from, to] = partBounds(n);
    sentByPart.set(n, to - from);
  }

  const pending = [];
  for (let n = 1; n <= numParts; n++) if (!doneEtags.has(n)) pending.push(n);

  let retrying = 0;
  const report = (phase) => {
    if (settled) return;
    let loaded = 0;
    for (const v of sentByPart.values()) loaded += v;
    onProgress({
      loaded, total: file.size,
      pct: file.size ? Math.min(100, Math.floor((loaded / file.size) * 100)) : 0,
      phase: phase || "uploading",
      partsDone: doneEtags.size, partsTotal: numParts,
      retrying,
    });
  };
  report(pending.length === numParts ? "uploading" : "resuming");

  /** Fetch a fresh presigned URL for one part. */
  const refreshUrl = async (n) => {
    const r = await api.post(
      `/api/admin/v2/matches/${matchId}/resume-multipart-upload-whole-match/`,
      { upload_id: uploadId, s3_key_within: s3KeyWithin, part_numbers: [n] },
    );
    const hit = (r.data.part_urls || []).find((p) => p.part_number === n);
    if (!hit) throw new Error(`could not re-presign part ${n}`);
    rememberUrls([hit], urlDeadline(r.data));
    return hit.url;
  };

  /**
   * The URL to PUT part `n` with — re-presigned first if the one we hold
   * is about to lapse. Parts late in a long upload are minted hours
   * after the ones at the front, so this is per-part, not per-upload.
   */
  const urlFor = async (n) => {
    const held = urlByPart.get(n);
    const usable = held
      && (held.expiresAt === null
        || Date.now() < held.expiresAt - URL_REFRESH_MARGIN_MS);
    return usable ? held.url : refreshUrl(n);
  };

  /** PUT one part, retrying transient failures. */
  const putPart = async (n) => {
    const [from, to] = partBounds(n);
    let lastErr;
    for (let attempt = 1; attempt <= PART_RETRY_MAX; attempt++) {
      throwIfAborted();
      try {
        const url = await urlFor(n);
        // Re-slice every attempt: a consumed Blob stream can't be replayed.
        const blob = file.slice(from, to);
        const r = await rawPut(url, blob, {
          signal,
          onUploadProgress: (ev) => {
            sentByPart.set(n, ev.loaded || 0);
            report();
          },
        });
        const etag = r.headers?.etag || r.headers?.ETag || r.headers?.["etag"];
        if (!etag) {
          // Bucket CORS must expose ETag; without it CompleteMultipartUpload
          // can never be assembled, so failing loudly here beats a confusing
          // 400 at the end.
          const e = new Error(
            `S3 part ${n} response exposed no ETag header — the zcd-recordings ` +
            "CORS rule needs ExposeHeaders: ['ETag'].",
          );
          e.__zcdFatal = true;
          throw e;
        }
        doneEtags.set(n, etag);
        sentByPart.set(n, to - from);
        if (attempt > 1) retrying = Math.max(0, retrying - 1);
        report();
        return;
      } catch (e) {
        lastErr = e;
        if (isCancel(e)) { retrying = Math.max(0, retrying - 1); throw cancelled(); }
        if (attempt === 1) retrying += 1;
        // Expired signature → new URL, and that attempt doesn't count
        // against the transient-failure budget.
        if (isExpiredUrl(e)) {
          urlByPart.delete(n);   // never hand the dead URL back out
          try { await refreshUrl(n); } catch { /* fall through to backoff */ }
        } else if (!isTransient(e)) {
          retrying = Math.max(0, retrying - 1);
          throw e;
        }
        sentByPart.set(n, 0);          // that part's bytes didn't land
        report();
        if (attempt < PART_RETRY_MAX) {
          await sleep(BACKOFF_BASE_MS * 2 ** (attempt - 1));
        }
      }
    }
    retrying = Math.max(0, retrying - 1);
    throw lastErr;
  };

  // ── Run the pending parts with bounded concurrency ────────────────
  const queue = pending.slice();
  const workers = Array.from(
    { length: Math.max(1, Math.min(concurrency, queue.length || 1)) },
    async () => {
      for (;;) {
        const n = queue.shift();
        if (n === undefined) return;
        await putPart(n);
      }
    },
  );

  try {
    await Promise.all(workers);
  } catch (e) {
    // Stop the other workers from starting new parts, then surface a
    // resumable error rather than aborting the upload in S3.
    queue.length = 0;
    settled = true;
    e.uploadId = uploadId;
    e.s3KeyWithin = s3KeyWithin;
    e.resumable = !e.__zcdCancelled;
    e.partsDone = doneEtags.size;
    e.partsTotal = numParts;
    // Record how far we got so the Resume banner can say it out loud.
    let banked = 0;
    for (const n of doneEtags.keys()) {
      const [from, to] = partBounds(n);
      banked += to - from;
    }
    saveResumeSlot(matchId, {
      uploadId, s3KeyWithin, partSize, numParts,
      filename: file.name, totalSize: file.size,
      bytesUploaded: banked, partsUploaded: doneEtags.size,
      startedAt: new Date().toISOString(),
    });
    throw e;
  }

  // ── Complete ──────────────────────────────────────────────────────
  report("finalizing");
  const parts = Array.from(doneEtags.entries())
    .map(([PartNumber, ETag]) => ({ PartNumber, ETag }))
    .sort((a, b) => a.PartNumber - b.PartNumber);

  // Every part is in S3 by now, so whatever happens next the bytes are
  // safe — a failure here is worth resuming, never restarting.
  const failed = (err) => {
    settled = true;
    err.uploadId = uploadId;
    err.s3KeyWithin = s3KeyWithin;
    err.resumable = !err.__zcdCancelled;
    err.partsDone = doneEtags.size;
    err.partsTotal = numParts;
    return err;
  };

  let done = null;
  let pendingErr = null;
  try {
    done = await api.post(
      `/api/admin/v2/matches/${matchId}/complete-multipart-upload-whole-match/`,
      { upload_id: uploadId, s3_key_within: s3KeyWithin, parts },
      { timeout: COMPLETE_POST_TIMEOUT_MS },
    );
  } catch (e) {
    if (isCancel(e)) throw failed(cancelled());
    // A real rejection (400 malformed parts, 502 from S3) is a genuine
    // failure; a timeout or gateway error is not an answer at all.
    if (!isAssemblyPending(e)) throw failed(e);
    pendingErr = e;
  }

  // 202 = the backend handed the assembly to S3 and stopped waiting so
  // CloudFront could not cut it. Either way, watch for the object.
  if (pendingErr || done?.status === 202) {
    report("assembling");
    const master = await waitForMaster({
      api, matchId, s3KeyWithin, signal, onTick: () => report("assembling"),
    });
    if (!master) {
      if (signal?.aborted) throw failed(cancelled());
      const e = new Error(
        "S3 never finished assembling the master. Every part is still " +
        "uploaded — pick the same file again to resume and re-finish it.",
      );
      e.__zcdFatal = true;
      throw failed(e);
    }
    settled = true;
    clearResumeSlot(matchId);
    return {
      s3_key: master.s3_key || s3Key,
      s3_key_within: master.s3_key_within || s3KeyWithin,
    };
  }

  settled = true;
  clearResumeSlot(matchId);
  return {
    s3_key: done.data?.s3_key || s3Key,
    s3_key_within: done.data?.s3_key_within || s3KeyWithin,
  };
}

/**
 * Human-readable failure text. Axios reports a dropped connection as a
 * bare "Network Error" with no response, which told the operator nothing
 * about what to do next — so say it plainly and name the recovery.
 */
export function describeUploadError(e) {
  if (e?.__zcdCancelled) return "Upload cancelled.";
  const where = e?.partsTotal
    ? ` (${e.partsDone}/${e.partsTotal} parts uploaded)`
    : "";
  if (e?.response) {
    const code = e.response.status;
    const data = e.response.data;
    if (typeof data === "string" && data.includes("<Code>")) {
      const c = /<Code>([^<]+)<\/Code>/.exec(data);
      const m = /<Message>([^<]+)<\/Message>/.exec(data);
      return `S3 ${code}: ${(c && c[1]) || "?"} — ${(m && m[1]) || ""}${where}`;
    }
    if (data && typeof data === "object" && data.detail) {
      return `HTTP ${code}: ${data.detail}${where}`;
    }
    return `HTTP ${code}${where}`;
  }
  if (e?.__zcdFatal) return `${e.message}${where}`;
  return (
    `Connection to S3 dropped after ${PART_RETRY_MAX} retries${where}. ` +
    "Nothing already uploaded was lost — pick the same file again to resume."
  );
}
